import { useState, useEffect } from 'react';
import { useCountry } from '../hooks/useMorocco';
import { getAdminLocations, getScraperStatus, getDetections, AdminLocationInfo, ScraperStatus } from '../api/adminApi';
import { useAllCountries, CountryOverview } from '../hooks/useAllCountries';
import CountryCard from '../components/CountryCard';
import HealthDot from '../components/HealthDot';
import ConfirmSightingModal, { ModalMode } from '../components/ConfirmSightingModal';
import ConfirmMonthModal from '../components/ConfirmMonthModal';
import AdjustDateModal from '../components/AdjustDateModal';
import CountryHistoryModal from '../components/CountryHistoryModal';
import CountriesPage from './CountriesPage';
import DetectionsPage from './DetectionsPage';
import PipelineDebugPage from './PipelineDebugPage';
import InitializeModal from '../components/InitializeModal';
import SubscriptionsPage from './SubscriptionsPage';
import styles from './Dashboard.module.css';

interface Props { onLogout: () => void; }

type Filter = 'all' | 'needs-action' | 'confirmed' | 'errors' | 'stable' | 'not-initialized';

function filterLabel(f: Filter): string {
  return { all: 'All', 'needs-action': 'Needs Action', confirmed: 'Confirmed Today', errors: 'Errors', stable: 'Stable', 'not-initialized': 'Not Initialized' }[f];
}

function matchesFilter(ov: CountryOverview, filter: Filter): boolean {
  switch (filter) {
    case 'all':             return true;
    case 'needs-action':    return ov.cardStatus === 'yellow';
    case 'confirmed':       return ov.cardStatus === 'green';
    case 'errors':          return ov.cardStatus === 'red';
    case 'stable':          return ov.cardStatus === 'white';
    case 'not-initialized': return ov.cardStatus === 'gray';
  }
}

function useUtcClock(): string {
  const [utc, setUtc] = useState(() => new Date().toUTCString().slice(17, 22));
  useEffect(() => {
    const id = setInterval(() => setUtc(new Date().toUTCString().slice(17, 22)), 10_000);
    return () => clearInterval(id);
  }, []);
  return utc;
}

export default function Dashboard({ onLogout }: Props) {
  const [view, setView] = useState<'dashboard' | 'countries' | 'detections' | 'pipeline' | 'waitlist'>('dashboard');
  const [allLocations, setAllLocations] = useState<AdminLocationInfo[]>([]);
  const [locLoading, setLocLoading] = useState(true);
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus | null>(null);
  const [pendingDetections, setPendingDetections] = useState(0);
  const [filter, setFilter] = useState<Filter>('all');
  const [inboxHighlight, setInboxHighlight] = useState<string | undefined>();
  const [pipelineHighlight, setPipelineHighlight] = useState<string | undefined>();
  const [selectedCode, setSelectedCode] = useState('MA');

  // Modal states
  const [confirmModal, setConfirmModal] = useState<CountryOverview | null>(null);
  const [adjustModal, setAdjustModal] = useState<CountryOverview | null>(null);
  const [historyModal, setHistoryModal] = useState<CountryOverview | null>(null);
  const [legacyModal, setLegacyModal] = useState<ModalMode | null>(null);
  const [initializeModal, setInitializeModal] = useState<{ code: string; name: string } | null>(null);

  // Hash-based routing + email deep-link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'detections') {
      setView('detections');
      setInboxHighlight(params.get('highlight') ?? undefined);
      window.history.replaceState({}, '', window.location.pathname + '#detections');
    } else {
      const hash = window.location.hash.slice(1);
      if (hash === 'countries' || hash === 'detections' || hash === 'pipeline' || hash === 'waitlist') {
        setView(hash as 'countries' | 'detections' | 'pipeline' | 'waitlist');
      }
    }
    function handleHashChange() {
      const h = window.location.hash.slice(1);
      if (h === 'countries' || h === 'detections' || h === 'pipeline' || h === 'waitlist') setView(h as 'countries' | 'detections' | 'pipeline' | 'waitlist');
      else setView('dashboard');
    }
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    getAdminLocations()
      .then((locs) => {
        setAllLocations(locs);
        const managed = locs.filter((l) => l.enabled && l.method === 'MOON_SIGHTING');
        if (managed.length > 0 && !managed.find((l) => l.code === selectedCode)) {
          setSelectedCode(managed[0].code);
        }
      })
      .finally(() => setLocLoading(false));
  }, []);

  useEffect(() => {
    function refresh() {
      getScraperStatus().then(setScraperStatus).catch(() => {});
      getDetections('PENDING').then(d => setPendingDetections(d.length)).catch(() => {});
    }
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, []);

  const { overviews, loading: overviewsLoading, refetch: refetchOverviews } = useAllCountries(allLocations);

  // For the legacy CONFIRM_START modal (initializing a country)
  const { refetch: refetchSelected } = useCountry(selectedCode);

  const utc = useUtcClock();

  // Summary bar counts
  const totalEnabled = allLocations.filter((l) => l.enabled && l.method !== 'CALCULATION').length;
  const needsAction = overviews.filter((o) => o.cardStatus === 'yellow').length;
  const inconsistent = overviews.filter((o) => o.cardStatus === 'red').length;
  const confirmedToday = overviews.filter((o) => o.cardStatus === 'green').length;
  const notInitialized = overviews.filter((o) => o.cardStatus === 'gray').length;

  const filters: Filter[] = ['all', 'needs-action', 'confirmed', 'errors', 'stable', 'not-initialized'];
  const filtered = overviews.filter((o) => matchesFilter(o, filter));

  function handleSuccess() {
    setConfirmModal(null);
    setAdjustModal(null);
    setLegacyModal(null);
    setInitializeModal(null);
    refetchOverviews();
    refetchSelected();
  }

  function refreshLocations() {
    getAdminLocations().then(setAllLocations);
  }

  function navigate(newView: typeof view) {
    if (view !== 'dashboard' && newView === 'dashboard') {
      refreshLocations();
    }
    setView(newView);
    window.location.hash = newView === 'dashboard' ? '' : newView;
  }

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.logo}>🌙</span>
          <span className={styles.appName}>Hijriya Admin</span>
        </div>
        <nav className={styles.headerNav}>
          <button className={`${styles.navBtn} ${view === 'dashboard' ? styles.navActive : ''}`} onClick={() => navigate('dashboard')}>Control Tower</button>
          <button className={`${styles.navBtn} ${view === 'detections' ? styles.navActive : ''}`} onClick={() => navigate('detections')}>
            Inbox{pendingDetections > 0 && <span className={styles.navBadge}>{pendingDetections}</span>}
          </button>
          <button className={`${styles.navBtn} ${view === 'countries' ? styles.navActive : ''}`} onClick={() => navigate('countries')}>Countries</button>
          <button className={`${styles.navBtn} ${view === 'pipeline' ? styles.navActive : ''}`} onClick={() => navigate('pipeline')}>Pipeline</button>
          <button className={`${styles.navBtn} ${view === 'waitlist' ? styles.navActive : ''}`} onClick={() => navigate('waitlist')}>Waitlist</button>
        </nav>
        <div className={styles.headerRight}>
          <HealthDot status={scraperStatus} />
          <button className={styles.logoutBtn} onClick={onLogout}>Sign out</button>
        </div>
      </header>

      {view === 'countries' && <CountriesPage onLocationChanged={refreshLocations} />}
      {view === 'detections' && (
        <DetectionsPage
          allLocations={allLocations}
          highlightId={inboxHighlight}
          onViewRun={(runId) => { setPipelineHighlight(runId); navigate('pipeline'); }}
        />
      )}
      {view === 'pipeline' && <PipelineDebugPage highlightRunId={pipelineHighlight} />}
      {view === 'waitlist' && <SubscriptionsPage />}

      {view === 'dashboard' && (
        <>
          {/* Sticky summary bar */}
          <div className={styles.summaryBar}>
            <Metric label="Countries" value={totalEnabled} />
            <MetricAlert label="Needs Action" value={needsAction} color={needsAction > 0 ? '#f59e0b' : undefined} />
            <MetricAlert label="Inconsistent" value={inconsistent} color={inconsistent > 0 ? '#ef4444' : undefined} />
            <Metric label="Confirmed Today" value={confirmedToday} color={confirmedToday > 0 ? '#16a34a' : undefined} />
            <Metric label="Not Initialized" value={notInitialized} color={notInitialized > 0 ? '#94a3b8' : undefined} />
            <div className={styles.utcClock}>
              <span className={styles.utcLabel}>UTC</span>
              <span className={styles.utcTime}>{utc}</span>
            </div>
          </div>

          <main className={styles.main}>
            {locLoading || overviewsLoading ? (
              <div className={styles.loadingWrap}><div className={styles.spinner} /></div>
            ) : (
              <>
                {/* Filter tabs */}
                <div className={styles.filters}>
                  {filters.map((f) => {
                    const count = f === 'all' ? overviews.length : overviews.filter((o) => matchesFilter(o, f)).length;
                    return (
                      <button
                        key={f}
                        className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ''}`}
                        onClick={() => setFilter(f)}
                      >
                        {filterLabel(f)}
                        {count > 0 && <span className={styles.filterCount}>{count}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Grid */}
                {filtered.length === 0 ? (
                  <div className={styles.emptyGrid}>
                    {overviews.length === 0
                      ? <p>No moon-sighting countries enabled. <button className={styles.linkBtn} onClick={() => navigate('countries')}>Manage Countries →</button></p>
                      : <p>No countries match this filter.</p>
                    }
                  </div>
                ) : (
                  <div className={styles.grid}>
                    {filtered.map((ov) => (
                      <CountryCard
                        key={ov.location.code}
                        overview={ov}
                        selected={ov.location.code === selectedCode}
                        onClick={() => setSelectedCode(ov.location.code)}
                        onConfirm={() => { setSelectedCode(ov.location.code); setConfirmModal(ov); }}
                        onAdjust={() => { setSelectedCode(ov.location.code); setAdjustModal(ov); }}
                        onHistory={() => setHistoryModal(ov)}
                        onInitialize={() => {
                          setSelectedCode(ov.location.code);
                          setInitializeModal({ code: ov.location.code, name: ov.location.name });
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </main>

          {confirmModal && confirmModal.status && (
            <ConfirmMonthModal
              locationCode={confirmModal.location.code}
              locationName={confirmModal.location.name}
              status={confirmModal.status}
              months={confirmModal.months}
              onClose={() => setConfirmModal(null)}
              onSuccess={handleSuccess}
            />
          )}

          {adjustModal && (
            <AdjustDateModal
              locationCode={adjustModal.location.code}
              locationName={adjustModal.location.name}
              status={adjustModal.status}
              months={adjustModal.months}
              onClose={() => setAdjustModal(null)}
              onSuccess={handleSuccess}
            />
          )}

          {historyModal && (
            <CountryHistoryModal
              locationCode={historyModal.location.code}
              locationName={historyModal.location.name}
              onClose={() => setHistoryModal(null)}
            />
          )}

          {legacyModal && (
            <ConfirmSightingModal
              mode={legacyModal}
              locationCode={selectedCode}
              authority={allLocations.find((l) => l.code === selectedCode)?.authority ?? ''}
              onClose={() => setLegacyModal(null)}
              onSuccess={handleSuccess}
            />
          )}

          {initializeModal && (
            <InitializeModal
              locationCode={initializeModal.code}
              locationName={initializeModal.name}
              onClose={() => setInitializeModal(null)}
              onSuccess={handleSuccess}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Summary metric components ─────────────────────────────────────────────────

function Metric({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className={styles.metric}>
      <span className={styles.metricValue} style={color ? { color } : undefined}>{value}</span>
      <span className={styles.metricLabel}>{label}</span>
    </div>
  );
}

function MetricAlert({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className={`${styles.metric} ${value > 0 ? styles.metricHighlight : ''}`}>
      <span className={styles.metricValue} style={color ? { color } : undefined}>{value}</span>
      <span className={styles.metricLabel}>{label}</span>
    </div>
  );
}
